// ====================================================================
// PROP PALETTE — single source of truth for authorable world props
// ====================================================================
// The 17 raw PROP_TYPES in chunks.js collapse into ~15 *authorable* palette
// items: a `tree` is trunk+foliage(+collision), a `flower` is
// flowerStem+flowerHead. Each descriptor knows how a single placement expands
// into the (rawType, descriptor) parts addProp/addPropLive consume, and how to
// roll any per-instance random fields that must persist so a reloaded/baked
// world reproduces exactly what was placed (flower colour is visible; tree sway
// phase is not, so it is intentionally left to re-randomise).
//
// This module is core-safe: no THREE, no DOM, no build-flag assumptions, no
// top-level side effects. It is imported by the editor (placement UI) and by
// the baked-world startup path, so both expand props the same way and can never
// drift. The expand() field shapes mirror each compose() in chunks.js exactly
// (pathStone reads d.r, fenceRail reads d.len, flower reads d.h/d.s/color).

// Bright blossom colours, matching the meadow accents used by buildFlowerPatch.
export const FLOWER_COLORS = [0xff7eb6, 0xffd23f, 0xff5d5d, 0x7ec8ff, 0xffffff, 0xb088ff];

// Helper: a simple yaw'd single-part prop (the common case for clutter props).
function yawProp(type) {
  return (d) => [[type, { x: d.x, z: d.z, scale: d.scale, yaw: d.yaw }]];
}

// Ordered for the dropdown. `key` is what the edit layer stores as a prop's
// `type`; `hasCollision` routes a placement to ctx.placeTree (trunk+foliage+
// obstacle) instead of raw addProp.
export const PROP_PALETTE = [
  {
    key: 'tree', label: '🌳 Tree', hasCollision: true,
    defaultScale: 1, scaleRange: [0.5, 2],
    // Routed to ctx.placeTree — expand is unused for collision props but kept
    // for the baked path, which registers the obstacle itself.
    expand: (d) => [
      ['trunk', { x: d.x, z: d.z, scale: d.scale }],
      ['foliage', { x: d.x, z: d.z, scale: d.scale, phase: d.phase || 0 }],
    ],
  },
  { key: 'bush', label: '🌿 Bush', defaultScale: 1, scaleRange: [0.5, 2],
    expand: (d) => [['bush', { x: d.x, z: d.z, scale: d.scale }]] },
  { key: 'rock', label: '🪨 Rock', defaultScale: 1, scaleRange: [0.5, 2.5], expand: yawProp('rock') },
  { key: 'mushroom', label: '🍄 Mushroom', defaultScale: 1, scaleRange: [0.5, 1.6], expand: yawProp('mushroom') },
  {
    key: 'flower', label: '🌸 Flower', defaultScale: 1, scaleRange: [0.6, 1.8],
    // Colour is the one visible per-instance random — roll it once and persist
    // it on the record so reloads/baked worlds keep the same bloom.
    roll: (rng) => ({ color: FLOWER_COLORS[(rng() * FLOWER_COLORS.length) | 0] }),
    expand: (d) => {
      const h = 0.45 * d.scale;
      return [
        ['flowerStem', { x: d.x, z: d.z, h }],
        ['flowerHead', { x: d.x, z: d.z, h, s: 0.7 * d.scale, color: d.color }],
      ];
    },
  },
  { key: 'grassTuft', label: '🌾 Grass', defaultScale: 1, scaleRange: [0.5, 1.6], expand: yawProp('grassTuft') },
  { key: 'crate', label: '📦 Crate', defaultScale: 1, scaleRange: [0.6, 1.6], expand: yawProp('crate') },
  { key: 'barrel', label: '🛢 Barrel', defaultScale: 1, scaleRange: [0.6, 1.6], expand: yawProp('barrel') },
  { key: 'mailbox', label: '📫 Mailbox', defaultScale: 1, scaleRange: [0.6, 1.6], expand: yawProp('mailbox') },
  { key: 'flowerBox', label: '🪴 Flower box', defaultScale: 1, scaleRange: [0.6, 1.6], expand: yawProp('flowerBox') },
  { key: 'tinyFlag', label: '🚩 Flag', defaultScale: 1, scaleRange: [0.6, 1.6], expand: yawProp('tinyFlag') },
  { key: 'marketBasket', label: '🧺 Basket', defaultScale: 1, scaleRange: [0.6, 1.6], expand: yawProp('marketBasket') },
  // ---- handmade kit props (chunks.js kit types, geometry from kit.glb) ----
  // Fixed scale: the collision boxes below are the models' real dims, and a
  // scaled visual over an unscaled box (or vice versa) is exactly the phantom
  // edge a kid falls through. `collision` is plain dims data (this module is
  // core-safe and can't reach into world/kit.js): the live editor registers a
  // movable obstacle, the baked path a static one. `place: 'lantern'` routes a
  // baked lantern through the full buildLantern (glow sprite + night light +
  // bump toggle); a live editor placement shows just the body — light behavior
  // arrives on the next reload of the baked/exported layer.
  { key: 'lantern', label: '🏮 Lantern', place: 'lantern',
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('lantern') },
  { key: 'platform_small', label: '🪜 Platform S', collision: { w: 0.9, h: 0.67, d: 0.85 },
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('platform_small') },
  { key: 'platform_mid', label: '🪜 Platform M', collision: { w: 0.9, h: 1.21, d: 0.85 },
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('platform_mid') },
  { key: 'platform_tall', label: '🪜 Platform L', collision: { w: 0.9, h: 1.66, d: 0.85 },
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('platform_tall') },
  // ---- assetpack2 kit props ----
  { key: 'platform_floating', label: '🛸 Platform Float', collision: { w: 0.91, h: 0.63, d: 0.91 },
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('platform_floating') },
  { key: 'bunny_hutch', label: '🐰 Bunny hutch', collision: { w: 0.85, h: 0.82, d: 0.55 },
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('bunny_hutch') },
  { key: 'haybale', label: '🌾 Hay bale', collision: { w: 0.72, h: 0.34, d: 0.35 },
    defaultScale: 1, scaleRange: [1, 1], expand: yawProp('haybale') },
  // Decorative, no collision: the maypole often stands on a path/plaza centre
  // where a footprint box would wall off the walkway (see villageDecor.js).
  { key: 'maypole', label: '🎀 Maypole', defaultScale: 1, scaleRange: [1, 1],
    expand: yawProp('maypole') },
  { key: 'fencePost', label: '🥢 Fence post', defaultScale: 1, scaleRange: [1, 1],
    expand: (d) => [['fencePost', { x: d.x, z: d.z }]] },
  { key: 'fenceRail', label: '➖ Fence rail', defaultScale: 1, scaleRange: [0.5, 3], defaultLen: 1,
    // The single scale slider drives rail length.
    expand: (d) => [['fenceRail', { x: d.x, z: d.z, len: d.len ?? d.scale ?? 1, yaw: d.yaw }]] },
  { key: 'pathStone', label: '⚪ Path stone', defaultScale: 1, scaleRange: [0.3, 2], defaultR: 1,
    // The scale slider drives the stone radius.
    expand: (d) => [['pathStone', { x: d.x, z: d.z, r: d.r ?? d.scale ?? 1 }]] },
];

// key -> descriptor, for O(1) lookup by the stored prop `type`.
export const PROP_PALETTE_BY_KEY = Object.fromEntries(PROP_PALETTE.map((p) => [p.key, p]));

// Expand a stored placement record into its (rawType, descriptor) parts. Falls
// back to a bush so an unknown legacy type never throws on replay.
export function expandProp(rec) {
  const desc = PROP_PALETTE_BY_KEY[rec.type] || PROP_PALETTE_BY_KEY.bush;
  return desc.expand(rec);
}

// Roll the persistent per-instance random fields for a fresh placement of
// `type` (e.g. flower colour). Returns an object to spread onto the record.
export function rollProp(type, rng = Math.random) {
  const desc = PROP_PALETTE_BY_KEY[type];
  return desc && desc.roll ? desc.roll(rng) : {};
}
